import { useCallback, useEffect, useState } from "react";
import { FiRefreshCw } from "react-icons/fi";
import { getApiUrl } from "../../../../config/api";
import { useAuth } from "../../../../firebase/AuthContext";
import {
  CycleCard,
  WeekCard,
} from "../../../../components/usageBilling/UsageSummary";
import UsageCurrencySwitch from "../../../../components/usageBilling/UsageCurrencySwitch";
import SpendBreakdown from "../../../../components/usageBilling/SpendBreakdown";
import WeeklyHistoryChart from "../../../../components/usageBilling/WeeklyHistoryChart";
import useUsdTwdRate from "../../../../components/usageBilling/useUsdTwdRate";
import {
  fillWeeks,
  formatDay,
  formatTwdFromUsd,
  formatUsdWithCode,
  resetDay,
} from "../../../../components/usageBilling/usageFormat";

import LimitAmountField, { useLimitAmount } from "../../../../components/usageBilling/LimitAmountField";

// The bot's own AI usage: every visitor on every site it's embedded on, and
// the "Try it live" preview, count against one quota. Shown with
// webchatbot.billing.read (or billing.view_all); setting the limit needs
// webchatbot.billing.manage (or billing.limits.manage). The backend checks the
// same roles.
//
// The shared usage cards are dark-mode aware and this modal isn't, so the tab
// gives its body the dark background itself.
const BOT_API = `${getApiUrl()}/api/bot-dashboard`;
const HISTORY_WEEKS = 8;

const UsageBillingTab = ({ bot, language }) => {
  const zh = language === "zh";
  const { currentUser, hasRole } = useAuth();
  const canEdit = hasRole(["webchatbot.billing.manage", "billing.limits.manage"]);

  const [currency, setCurrency] = useState("USD");
  const rateState = useUsdTwdRate();
  const twdRate = rateState.data?.rate;
  const displayCurrency = currency === "TWD" && rateState.data ? "TWD" : "USD";
  const formatConvertedMoney = useCallback(
    (value) => formatTwdFromUsd(value, twdRate),
    [twdRate],
  );
  const formatMoney =
    displayCurrency === "TWD" ? formatConvertedMoney : formatUsdWithCode;

  const [usage, setUsage] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUsage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(
        `${BOT_API}/${encodeURIComponent(bot.bot_id)}/usage?weeks=${HISTORY_WEEKS}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // A proxy error page (a 502 from Cloud Run, say) isn't JSON.
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(data.error || `Request failed (${response.status})`);
      }
      setUsage(data);
    } catch (err) {
      console.error("[UsageBillingTab] Failed to load bot usage:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser, bot.bot_id]);

  useEffect(() => {
    loadUsage();
  }, [loadUsage]);

  const weeks = usage ? fillWeeks(usage, usage.history, HISTORY_WEEKS) : [];
  const neverUsed = usage && usage.turns === 0 && usage.history.length === 0;

  return (
    <div className="-m-6 p-6 min-h-full space-y-4 dark:bg-gray-800">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {zh ? "用量與計費" : "Usage & Billing"}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {zh
              ? "這個聊天機器人回答訪客所用的 AI 費用，涵蓋所有嵌入的網站及「立即試用」預覽。每月額度以 28 天為一個週期，按週分配。"
              : "What this chatbot's answers cost in AI, on every site it's embedded on and in the Try it live preview. Monthly limits run per 28-day cycle and are paced weekly."}
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            loadUsage();
            rateState.refresh();
          }}
          disabled={loading}
          className="p-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          title={zh ? "重新整理" : "Refresh"}
          aria-label={zh ? "重新整理" : "Refresh"}
        >
          <FiRefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      <UsageCurrencySwitch
        currency={displayCurrency}
        onChange={setCurrency}
        rateState={rateState}
        language={language}
      />

      {!usage && loading && (
        <div className="flex flex-col items-center gap-3 py-16">
          <div className="w-8 h-8 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {zh ? "正在載入用量..." : "Loading usage..."}
          </p>
        </div>
      )}

      {error && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-300">
          <span>
            {zh ? "無法載入用量：" : "Couldn't load usage: "}
            {error}
          </span>
          <button
            type="button"
            onClick={loadUsage}
            disabled={loading}
            className="shrink-0 font-medium underline disabled:opacity-50"
          >
            {zh ? "重試" : "Retry"}
          </button>
        </div>
      )}

      {/* On a reload the last result stays, dimmed, rather than flashing. */}
      {usage && (
        <div
          className={`space-y-4 transition-opacity ${loading ? "opacity-60" : ""}`}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <WeekCard
              week={usage}
              enforced={usage.enforced}
              formatMoney={formatMoney}
              language={language}
              forAdmin
            />
            <CycleCard
              usage={usage}
              language={language}
              forAdmin
              formatMoney={formatMoney}
            />
          </div>

          <BotLimitSection
            bot={bot}
            usage={usage}
            canEdit={canEdit}
            currentUser={currentUser}
            onSaved={loadUsage}
            formatMoney={formatMoney}
            currency={displayCurrency}
            rateState={rateState}
            language={language}
          />

          {neverUsed ? (
            <p className="rounded-xl border border-dashed border-gray-300 dark:border-gray-600 px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              {zh
                ? "這個聊天機器人還沒有回答過任何問題。"
                : "This chatbot hasn't answered any questions yet."}
            </p>
          ) : (
            <div className="grid gap-4 md:grid-cols-5">
              <div className="min-w-0 md:col-span-2">
                <SpendBreakdown
                  breakdown={usage.breakdown_usd}
                  formatMoney={formatMoney}
                  title={zh ? "本週費用明細" : "Where it went this week"}
                  language={language}
                />
              </div>
              <section className="min-w-0 md:col-span-3 rounded-xl border border-gray-200 dark:border-gray-700 p-4 flex flex-col gap-3">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                  {zh ? "過去幾週" : "Past weeks"}
                </h3>
                {weeks.length > 1 ? (
                  <WeeklyHistoryChart
                    weeks={weeks}
                    language={language}
                    formatMoney={formatMoney}
                  />
                ) : (
                  <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    {zh
                      ? `過去幾週的用量將於${formatDay(resetDay(usage.week_end), language)}起顯示在這裡。`
                      : `Past weeks appear here from ${formatDay(resetDay(usage.week_end), language)}.`}
                  </p>
                )}
              </section>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// The bot's monthly limit and, for those who may set it, the editor. A bot
// has one limit and no overrides; without one its AI use is unlimited.
function BotLimitSection({
  bot,
  usage,
  canEdit,
  currentUser,
  onSaved,
  language,
  formatMoney,
  currency,
  rateState,
}) {
  const zh = language === "zh";
  const limit = usage.monthly_limit_usd;
  const draft = useLimitAmount({ initialCurrency: currency, rateState });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const resets = formatDay(resetDay(usage.week_end), language);

  const save = async (value) => {
    setSaving(true);
    setError(null);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(
        `${BOT_API}/${encodeURIComponent(bot.bot_id)}/usage-limit`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ monthly_usage_limit_usd: value }),
        },
      );
      // A proxy error page (a 502 from Cloud Run, say) isn't JSON.
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(data.error || `Request failed (${response.status})`);
      }
      draft.setText("");
      setConfirmRemove(false);
      onSaved();
    } catch (err) {
      console.error("[UsageBillingTab] Failed to save bot usage limit:", err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 flex flex-col gap-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            {zh ? "每月額度（每 28 天）" : "Monthly limit (per 28-day cycle)"}
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {zh
              ? "所有訪客共用。每週可用四分之一，未用完的部分結轉到同週期的下一週。變更立即生效。"
              : "Every visitor shares it. A quarter is available each week, and what's left carries over within the cycle. Changes apply immediately."}
          </p>
        </div>
        {limit === null ? (
          <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-amber-100 text-amber-800">
            {zh ? "未設定額度" : "No limit"}
          </span>
        ) : (
          <span className="text-lg font-semibold tabular-nums text-gray-900 dark:text-white">
            {formatMoney(limit)}
          </span>
        )}
      </div>

      {limit === null && (
        <p className="text-xs text-amber-700 dark:text-amber-400">
          {zh
            ? "沒有設定額度，這個聊天機器人的 AI 用量不受限制。"
            : "No limit is set, so this chatbot's AI use is unlimited."}
        </p>
      )}
      {limit !== null && !usage.enforced && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {zh
            ? "額度限制目前關閉中：超過額度也不會停止回答。"
            : "Enforcement is off: going over the limit doesn't stop the chatbot answering."}
        </p>
      )}
      {usage.over_quota && usage.enforced && (
        <p className="text-xs text-gray-600 dark:text-gray-300">
          {zh
            ? `在${resets}之前，訪客提問時會被請到「聯絡我們」留下聯絡方式。`
            : `Until ${resets}, visitors who ask a question are asked to leave their details through Contact us.`}
        </p>
      )}

      {canEdit && (
        <form
          className="space-y-3 pt-1"
          onSubmit={(e) => {
            e.preventDefault();
            if (draft.canSave && !saving) save(draft.usdLimit);
          }}
        >
          <LimitAmountField
            id="bot-limit"
            label={
              limit === null
                ? zh
                  ? "設定額度"
                  : "Set a limit"
                : zh
                  ? "變更額度"
                  : "Change the limit"
            }
            draft={draft}
            rateState={rateState}
            saving={saving}
            language={language}
          />
          <div className="flex flex-wrap items-center justify-between gap-3">
            {limit !== null ? (
              confirmRemove ? (
                <span className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                  {zh ? "移除後將不受限制。" : "The chatbot will have no limit."}
                  <button
                    type="button"
                    onClick={() => save(null)}
                    disabled={saving}
                    className="font-medium text-red-600 dark:text-red-400 hover:underline disabled:opacity-50"
                  >
                    {zh ? "移除" : "Remove"}
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmRemove(true)}
                  disabled={saving}
                  className="text-sm font-medium text-red-600 dark:text-red-400 hover:underline disabled:opacity-50"
                >
                  {zh ? "移除額度" : "Remove limit"}
                </button>
              )
            ) : (
              <span />
            )}
            <button
              type="submit"
              disabled={!draft.canSave || saving}
              className="px-4 py-1.5 rounded-lg text-sm font-medium bg-orange-500 hover:bg-orange-600 text-white disabled:bg-gray-200 disabled:text-gray-400 dark:disabled:bg-gray-700 disabled:cursor-not-allowed"
            >
              {saving ? (zh ? "儲存中..." : "Saving...") : zh ? "儲存" : "Save"}
            </button>
          </div>
        </form>
      )}
      {canEdit && draft.isZero && (
        <p className="text-xs text-amber-700 dark:text-amber-400">
          {zh
            ? "0 表示限制開啟後，這個聊天機器人將不再回答訪客。"
            : "0 stops this chatbot answering visitors once enforcement is on."}
        </p>
      )}
      {error && (
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
    </section>
  );
}

export default UsageBillingTab;
